import { useEffect, useRef, useState, type RefObject } from 'react';
import Hls from 'hls.js';
import { tokenStorage, API_BASE } from '@/services/apiClient';
import { getDeviceId } from '@/services/deviceId';
import { playbackHeaders } from '@/services/playbackHeaders';
import { isScreenLimitBody } from '@/services/screenLimit';
import { useAppDispatch } from '@/store';
import { screenLimitReached } from '@/store/slices/screenSlice';
import { buildP2pCoreConfig, type P2pMode } from '@/lib/p2pConfig';
import { attachNativeSeekDiag } from './nativeHlsSeekDiag';

// P2P Media Loader is loaded lazily — only when `p2pMode` is set. This keeps
// it out of the main bundle for solo VOD viewers (~150KB minified saved) AND
// avoids loading its `debug` CJS dependency during tests that don't exercise
// the P2P path (happy-dom + vite-node's ESM resolution chokes on the package's
// default-export-only `debug` import).
type P2pEngineMixin = {
  injectMixin: (hls: typeof Hls) => new (config: ConstructorParameters<typeof Hls>[0] & { p2p?: unknown }) => Hls & { p2pEngine: P2pEngineInstance };
};
type P2pEngineInstance = {
  addEventListener: (
    event: 'onChunkDownloaded' | 'onChunkUploaded' | 'onPeerConnect' | 'onPeerClose',
    cb: (...args: unknown[]) => void,
  ) => void;
};
let p2pEngineModulePromise: Promise<P2pEngineMixin> | null = null;
function loadP2pEngine(): Promise<P2pEngineMixin> {
  if (!p2pEngineModulePromise) {
    p2pEngineModulePromise = import('p2p-media-loader-hlsjs')
      .then((mod) => mod.HlsJsP2PEngine as unknown as P2pEngineMixin);
  }
  return p2pEngineModulePromise;
}

/** Stats surface for the P2P badge — populated from `HlsJsP2PEngine` events. */
export interface P2pStats {
  /** Number of currently-connected peers across all swarms in this hls instance. */
  peers: number;
  /** Bytes downloaded from peers since this instance started. */
  bytesFromPeers: number;
  /** Bytes downloaded from origin since this instance started. */
  bytesFromOrigin: number;
  /** Bytes uploaded to peers since this instance started. */
  bytesUploaded: number;
}

const EMPTY_STATS: P2pStats = { peers: 0, bytesFromPeers: 0, bytesFromOrigin: 0, bytesUploaded: 0 };

export interface UseHlsPlayerArgs {
  /** Absolute playlist URL (must include API_BASE; HLS.js resolves segments against this). */
  playlistUrl: string;
  videoRef: RefObject<HTMLVideoElement>;
  /** Bumping this re-runs the effect — used by Watch's manual "Retry" path. */
  retryToken?: number;
  /**
   * Re-keys the effect when the underlying playlist (or source preference)
   * changes. Pass a stable string per (ref, source) pair to avoid teardown
   * thrash on unrelated re-renders.
   */
  key?: string;
  /**
   * When true, opt into live-tuned behavior: auto-recover from transient
   * network/media errors via Hls.startLoad / Hls.recoverMediaError before
   * surfacing a fatal overlay, larger live sync window, longer retry budgets.
   * VOD callers (movies/TV/anime) leave this false so a fatal error fails fast
   * and lets the chain advance to the next provider.
   */
  live?: boolean;
  /**
   * When true, ask HLS.js to hold a much larger forward buffer (default
   * 30s → 240s, max 120s → 900s, back-buffer 60s → 180s). Trades memory + a
   * bit more CPU for resilience on flaky / mobile networks where the
   * baseline 30s isn't enough to ride out a 60s upstream blip. Wired to a
   * user-visible "Extra buffer" toggle in Watch.
   */
  extraBuffer?: boolean;
  /**
   * P2P swarm mode. When set, HLS.js is constructed via
   * `HlsJsP2PEngine.injectMixin` so segments can be fetched from peers in
   * the same swarm (peers discovered via public WebTorrent trackers, segment
   * data exchanged over WebRTC). Leave undefined to use plain hls.js with no
   * peer involvement.
   *
   * - `{ kind: 'lobby', ... }` for lobby members watching together
   * - `{ kind: 'sports', ... }` for live sports viewers (solo or in lobby)
   */
  p2pMode?: P2pMode | null;
  /**
   * When true (VOD callers), a fatal *network* error mid-playback (e.g. a
   * transient 502 on a segment through /api/stream/proxy) is auto-recovered in
   * place — `startLoad(currentPosition)` with a bounded attempt budget — BEFORE
   * surfacing the fatal overlay, so the viewer resumes from where they were
   * instead of being dumped to a manual "Retry from the beginning". Fatal
   * *media* errors still surface immediately so `useSourceFailover` can advance
   * to the next provider. No effect when `live` is true (live already recovers
   * at the live edge). Default false preserves the fail-fast VOD contract for
   * callers that don't opt in.
   */
  autoRecoverNetwork?: boolean;
  /**
   * Seconds. When > 0, the Hls instance is seeded with this `startPosition` and
   * the video element is seeked here once the manifest parses, so a hard reload
   * (the manual Retry button bumping `retryToken`) resumes from the saved
   * playhead rather than restarting the title at 0. Watch captures the playhead
   * into this at Retry-click time.
   */
  resumePosition?: number;
  /**
   * When true, treat `playlistUrl` as a THIRD-PARTY cross-origin HLS URL to be
   * loaded with NO credentials (Live TV client-direct playback, sub-project #2):
   *   - skip the mp4-envelope probe (that only detects OUR backend's
   *     `X-Stream-Container` header; a third-party HLS URL is always HLS, and the
   *     probe would otherwise send our auth headers off-origin), and
   *   - attach NO `Authorization` / device headers to any request, so the
   *     manifest + segments are simple, credential-free cross-origin GETs.
   * This prevents leaking our session token to an external CDN and avoids a
   * per-segment CORS preflight. Default false = the existing same-origin proxy
   * behavior (auth headers attached, probe runs).
   */
  crossOriginNoCreds?: boolean;
}

interface UseHlsPlayerResult {
  /** Fatal error type from Hls.js — `null` until something goes wrong. */
  fatal: string | null;
  /** Live Hls instance — exposed for callers that need to call e.g. recoverMediaError. */
  hls: Hls | null;
  /** Rolling P2P stats — zero when `p2pMode` is undefined. */
  p2pStats: P2pStats;
}

/**
 * Match any of the backend's signed-proxy segment routes that are sig-authed
 * via the URL itself (no Bearer required, anonymous routes by design):
 *   /api/stream/proxy/:streamKey/:sig/:b64Path           (movie/TV/anime HLS)
 *   /api/sports-stream/proxy/:streamKey/:sig/:b64Path    (sports HLS, mirror)
 *   /api/sports-dash-seg/:streamKey/:sig/:encodedBase/*  (bintvdrm DASH)
 *
 * Exported for unit testing — callers should not need this directly.
 */
export function isSignedProxyUrl(url: string): boolean {
  return /\/api\/(?:stream|sports-stream)\/proxy\//.test(url)
    || /\/api\/sports-dash-seg\//.test(url);
}

/**
 * True when `url` targets our OWN API origin — the only place our session Bearer
 * and device header may be sent. When client-direct segments are enabled
 * (STREAMING_CLIENT_DIRECT_SEGMENT_HOSTS), the manifest carries absolute
 * third-party CDN URLs; attaching our headers there would leak the session token
 * off-origin AND force a CORS preflight per segment. Relative URLs and same-origin
 * (dev, API_BASE='') count as ours. Malformed URLs are treated as not-ours.
 */
export function isOwnApiUrl(url: string, apiBase: string): boolean {
  try {
    const pageOrigin = window.location.origin;
    return new URL(url, pageOrigin).origin === new URL(apiBase || pageOrigin, pageOrigin).origin;
  } catch {
    return false;
  }
}

/**
 * True on iOS Safari (iPhone/iPad, incl. iPadOS masquerading as "Macintosh"
 * with a touch screen). hls.js "works" there via Managed Media Source — it
 * parses the manifest (a duration appears) but does not reliably feed the
 * decoder, so VOD sits at 0. iOS Safari's *native* HLS is rock-solid, so we
 * hand it a header-free signed playlist URL (`/native.m3u8` → `/hls/...`) and
 * let `video.src` play it directly instead of running hls.js.
 */
export function shouldUseNativeHls(video: HTMLVideoElement | null): boolean {
  if (!video) return false;
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS =
    /iP(hone|ad|od)/.test(ua)
    || (/Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1);
  if (!isIOS) return false;
  // Only take the native path if the element actually advertises HLS support.
  return video.canPlayType('application/vnd.apple.mpegurl') !== '';
}

/** Cap auto-recovery attempts so a permanently-broken upstream still surfaces
 *  a fatal overlay instead of looping forever. Picked to cover the typical
 *  "stream advances by more than its window for a beat" cases (which resolve
 *  in 1-2 polls) without papering over a truly dead source. */
const MAX_AUTO_RECOVERY_ATTEMPTS = 5;
/** Cool-down between recovery attempts so we don't hammer the upstream. */
const RECOVERY_BACKOFF_MS = 1500;
/**
 * AAC-LC codec hint applied to levels that lack a CODECS attribute from the
 * manifest. Works around encoders that mis-signal AAC-LC streams as AAC Main
 * (`mp4a.40.1`) in the ADTS profile byte — Chromium MSE rejects mp4a.40.1
 * but accepts mp4a.40.2, and the underlying frames decode fine as LC.
 *
 * Wiring: `pickMostCompleteCodecName` in hls.js (utils/codecs.ts) returns the
 * level codec when the parsed codec fails `MediaSource.isTypeSupported`. So
 * seeding `level.audioCodec = 'mp4a.40.2'` makes mis-signaled Main fall back
 * to LC, while leaving valid parsed codecs (LC, HE-AAC mp4a.40.5, HE-AACv2
 * mp4a.40.29) untouched — those pass isTypeSupported and win the pick.
 *
 * Discovered 2026-05-27 while debugging animepahe playback firing
 * bufferAddCodecError on every Chromium client.
 */
const DEFAULT_AUDIO_CODEC_HINT = 'mp4a.40.2';

/**
 * Shared Hls.js bootstrap used by both Watch (movies/TV/anime) and WatchSports.
 * Owns the lifecycle of one Hls instance: construct → attachMedia → loadSource,
 * teardown on unmount or key change.
 *
 * Tunings are VOD-oriented by default:
 *   - lowLatencyMode false: VOD playlists, not LL-HLS chunks.
 *   - startLevel -1: let ABR pick the first level.
 *   - max/back-BufferLength: 30/120/60s — covers small network hiccups.
 *   - testBandwidth false: master playlist is small and proxied; skip the probe.
 *
 * Pass `live: true` for sports / live streams. That switches to a wider live
 * sync window AND adds bounded auto-recovery for transient fatal errors —
 * specifically the `levelParsingError` "media sequence mismatch" that Hls.js
 * fires when an upstream playlist's sequence number jumps faster than the
 * window overlap allows. Without recovery, a single ill-timed poll ends
 * playback even though the underlying stream is healthy.
 *
 * The xhrSetup hook attaches the Bearer token to the master playlist request
 * (the /api/stream/proxy/... segment route is intentionally anonymous, but
 * the header is harmless there).
 */
export function useHlsPlayer({
  playlistUrl,
  videoRef,
  retryToken = 0,
  key = playlistUrl,
  live = false,
  extraBuffer = false,
  p2pMode = null,
  autoRecoverNetwork = false,
  resumePosition = 0,
  crossOriginNoCreds = false,
}: UseHlsPlayerArgs): UseHlsPlayerResult {
  const dispatch = useAppDispatch();
  const hlsRef = useRef<Hls | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [hls, setHls] = useState<Hls | null>(null);
  const [p2pStats, setP2pStats] = useState<P2pStats>(EMPTY_STATS);

  useEffect(() => {
    let active = true;
    let recoveryAttempts = 0;
    let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
    // Guard so the resume seek fires only on the first successful manifest
    // parse of this load, not on every steady-state playlist refresh.
    let resumeApplied = false;
    // Captured so cleanup can destroy whatever was constructed regardless of
    // which branch (HLS / MP4) the async probe resolved into.
    let instance: Hls | null = null;
    let mp4VideoEl: HTMLVideoElement | null = null;
    let nativeVideoEl: HTMLVideoElement | null = null;
    let nativeErrorHandler: (() => void) | null = null;
    let detachNativeDiag: (() => void) | null = null;
    setFatal(null);
    setP2pStats(EMPTY_STATS);
    const videoEl = videoRef.current;
    if (!videoEl) return;
    // Empty playlistUrl means the caller wants the HLS path inactive —
    // typically WatchSports when the effective source is DASH (Shaka takes
    // over). Skip instance creation so we don't race with Shaka's attach().
    if (!playlistUrl) return;

    // Probe the playlist response so we can branch on container kind. Sports-VOD
    // (OK.ru-style progressive sources) returns `X-Stream-Container: mp4` plus a
    // JSON envelope `{containerKind, url}` pointing at a signed proxy MP4; the
    // browser then plays it natively via Range requests. Every other playlist
    // route (movies/TV/sports HLS) responds without the header, which we treat
    // as HLS for backwards compat. The probe adds one round-trip per playback
    // start on the HLS path (we discard the body and let hls.js refetch via its
    // own xhrLoader) — acceptable cost to keep the existing hls.js code path
    // unchanged.
    const startHlsPath = (): void => {
      if (!active) return;
      if (!Hls.isSupported()) {
        setFatal('HLS not supported in this browser');
        return;
      }

      const hlsBaseConfig = {
        lowLatencyMode: false,
        startLevel: -1,
        // Seed the initial playhead for a resume-on-retry reload. -1 = hls.js
        // default (start at the first fragment / 0). A positive value asks
        // hls.js to begin buffering at the saved position so a hard reload
        // doesn't restart the title from the beginning.
        startPosition: resumePosition > 0 ? resumePosition : -1,
        // "Extra buffer" mode bumps the forward window aggressively so an
        // upstream blip of up to ~4 min doesn't stall playback. backBufferLength
        // also grows so rewind stays smooth after the bump.
        maxBufferLength: extraBuffer ? 240 : 30,
        maxMaxBufferLength: extraBuffer ? 900 : 120,
        backBufferLength: extraBuffer ? 180 : 60,
        testBandwidth: false,
        // Live tunings: widen the sync window so a normal sequence advance
        // doesn't trip the "fell behind" guard, and give upstream-load retries
        // a generous budget so a transient 5xx doesn't end playback.
        ...(live
          ? {
              liveSyncDurationCount: 6,
              liveMaxLatencyDurationCount: 12,
              levelLoadingMaxRetry: 8,
              manifestLoadingMaxRetry: 4,
              fragLoadingMaxRetry: 6,
            }
          : {}),
        xhrSetup: (xhr: XMLHttpRequest, url: string) => {
          // Client-direct third-party CDN (Live TV #2): send NOTHING. Attaching
          // our Bearer would leak the session token off-origin and turn every
          // segment into a preflighted non-simple request. The CDN authenticates
          // by URL alone (public iptv-org stream), so no header is needed.
          if (crossOriginNoCreds) return;
          // Signed segment-proxy routes authenticate via the HMAC sig embedded
          // in the URL itself, NOT a Bearer token (see streaming.routes.ts
          // /proxy/:streamKey/:sig/:b64Path — "intentionally anonymous"). Sending
          // Authorization on these makes the cross-origin GET "non-simple" and
          // forces a CORS preflight per segment. Chrome's preflight cache is
          // keyed on exact URL, so every distinct segment misses and OPTIONS
          // fires alongside GET — doubling request count and adding a RTT per
          // segment. Skipping the header turns segments into simple GETs with
          // zero preflights (the 24h `Access-Control-Max-Age` cap on the
          // playlist route still helps that single request).
          if (isSignedProxyUrl(url)) return;
          // Client-direct third-party CDN segment (STREAMING_CLIENT_DIRECT_SEGMENT_HOSTS):
          // absolute, off our API origin. Sending our Bearer/device would leak the
          // session token off-origin and preflight every segment — send nothing.
          if (!isOwnApiUrl(url, API_BASE)) return;
          const token = tokenStorage.get();
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          // Screen gate keys off this header on the playlist.m3u8 load (hls.js
          // re-fetches the manifest via its own loader). Segments are excluded
          // by the isSignedProxyUrl guard above so they stay simple GETs.
          xhr.setRequestHeader('x-cobstream-device', getDeviceId());
        },
      };

      // ---------- HLS instance + (optional) P2P bootstrap ----------
      // For the plain (no-P2P) case we construct hls.js immediately and start
      // loading. For the P2P case we still construct hls.js + attachMedia +
      // wire events synchronously, but we DEFER `loadSource()` until P2PML
      // has loaded + its loader classes are wired onto `hls.config`. This is
      // load-bearing: P2PML's PlaylistLoader subclass calls
      // `engine.initHlsEvents()` in its constructor, which is the ONLY path
      // that registers the engine's `hlsManifestLoaded` / `hlsLevelLoaded`
      // hooks. If hls.js opens the master playlist with its DEFAULT
      // PlaylistLoader (i.e. loadSource fires before pLoader is swapped),
      // P2PML's tracker client never gets created and the badge stays at
      // "searching for peers…" forever — the symptom of the
      // 2026-05-25 "P2P isn't finding peers" complaint.
      const created = new Hls(hlsBaseConfig);
      instance = created;
      let bytesFromPeers = 0;
      let bytesFromOrigin = 0;
      let bytesUploaded = 0;
      let peers = 0;
      const flush = (): void => {
        if (!active) return;
        setP2pStats({ peers, bytesFromPeers, bytesFromOrigin, bytesUploaded });
      };
      hlsRef.current = created;
      setHls(created);
      created.attachMedia(videoEl);
      // Clear stale error AND reset the recovery counter when the manifest
      // re-parses cleanly. Also seed a level audioCodec hint on any level
      // that lacks one — see DEFAULT_AUDIO_CODEC_HINT for why. Mutating
      // levels[i].audioCodec here is safe because the first
      // FRAG_PARSING_INIT_SEGMENT (which reads it) doesn't fire until after
      // MANIFEST_PARSED returns.
      created.on(Hls.Events.MANIFEST_PARSED, (_evt: unknown, data: { levels?: Array<{ audioCodec?: string }> }) => {
        if (!active) return;
        recoveryAttempts = 0;
        setFatal(null);
        if (data?.levels) {
          for (const level of data.levels) {
            if (!level.audioCodec) {
              level.audioCodec = DEFAULT_AUDIO_CODEC_HINT;
            }
          }
        }
        // Resume-on-retry: once the fresh manifest is parsed, seek the media
        // element to the saved playhead and resume. startPosition (above)
        // usually handles this, but seeking explicitly covers builds where the
        // reused <video> element reset currentTime to 0 on the new source.
        if (resumePosition > 0 && !resumeApplied) {
          resumeApplied = true;
          const v = videoRef.current;
          if (v) {
            try {
              v.currentTime = resumePosition;
              Promise.resolve(v.play?.()).catch(() => { /* gesture may be required; harmless */ });
            } catch { /* ignore */ }
          }
        }
      });
      created.on(Hls.Events.LEVEL_LOADED, () => {
        if (!active) return;
        if (recoveryAttempts > 0) recoveryAttempts = 0;
      });
      created.on(Hls.Events.ERROR, (_evt: unknown, data: { fatal?: boolean; type?: string; details?: string }) => {
        if (!active || !data?.fatal) return;
        console.error('[HLS] fatal', data.type, data.details, data);

        // Auto-recovery before surfacing an overlay. NetworkError → startLoad
        // (re-request the playlist). MediaError → recoverMediaError (rebuild
        // the source buffer from the last keyframe). Both are idempotent.
        //
        //   - Live streams recover from BOTH error kinds and resume at the live
        //     edge (startLoad() with no position).
        //   - VOD streams that opted into `autoRecoverNetwork` recover from
        //     *network* errors (the transient-502 case) and resume from the
        //     current playhead (startLoad(position)) so the viewer keeps their
        //     place. VOD *media* errors still fail fast so useSourceFailover can
        //     advance to the next provider.
        const isMediaError = data.type === Hls.ErrorTypes.MEDIA_ERROR;
        const isNetworkError = data.type === Hls.ErrorTypes.NETWORK_ERROR;
        const shouldRecover = live || (autoRecoverNetwork && isNetworkError);
        if (shouldRecover && recoveryAttempts < MAX_AUTO_RECOVERY_ATTEMPTS) {
          recoveryAttempts += 1;
          const attempt = recoveryAttempts;
          // Capture the playhead now, before the reload, so VOD recovery
          // resumes where the viewer was rather than at 0. Null for live (the
          // live edge is correct there).
          const savedPosition = !live ? (videoRef.current?.currentTime ?? 0) : null;
          if (recoveryTimer) clearTimeout(recoveryTimer);
          recoveryTimer = setTimeout(() => {
            if (!active) return;
            try {
              if (isMediaError) {
                created.recoverMediaError();
              } else if (savedPosition != null && savedPosition > 0) {
                created.startLoad(savedPosition);
              } else {
                created.startLoad();
              }
              console.warn(`[HLS] auto-recovery attempt ${attempt}/${MAX_AUTO_RECOVERY_ATTEMPTS} (${data.type}/${data.details})`);
            } catch (err) {
              console.error('[HLS] recovery threw', err);
              setFatal(data.type ?? 'unknown error');
            }
          }, RECOVERY_BACKOFF_MS);
          return;
        }

        setFatal(data.type ?? 'unknown error');
      });

      if (p2pMode) {
        // Defer loadSource until P2PML has loaded + wired loaders onto config.
        void loadP2pEngine().then((P2pEngineCtor) => {
          if (!active) return;
          const EngineRuntime = P2pEngineCtor as unknown as new (cfg: { core: unknown }) => P2pEngineInstance & {
            bindHls: (hls: unknown) => void;
            getConfigForHlsJs: () => { fLoader: unknown; pLoader: unknown };
            destroy: () => void;
          };
          const engine = new EngineRuntime({ core: buildP2pCoreConfig(p2pMode) });
          const loaders = engine.getConfigForHlsJs();
          const cfg = (created as unknown as { config: Record<string, unknown> }).config;
          cfg.fLoader = loaders.fLoader;
          cfg.pLoader = loaders.pLoader;
          engine.bindHls(created);
          engine.addEventListener('onChunkDownloaded', ((bytes: unknown, source: unknown) => {
            if (typeof bytes !== 'number') return;
            if (source === 'p2p') bytesFromPeers += bytes;
            else bytesFromOrigin += bytes;
            flush();
          }) as (...args: unknown[]) => void);
          engine.addEventListener('onChunkUploaded', ((bytes: unknown) => {
            if (typeof bytes !== 'number') return;
            bytesUploaded += bytes;
            flush();
          }) as (...args: unknown[]) => void);
          engine.addEventListener('onPeerConnect', (() => { peers += 1; flush(); }) as (...args: unknown[]) => void);
          engine.addEventListener('onPeerClose', (() => { peers = Math.max(0, peers - 1); flush(); }) as (...args: unknown[]) => void);
        }).catch((err) => {
          // P2PML failed to load — silently fall back to plain hls.js. No
          // user-facing fatal because plain-HLS playback works regardless.
          // eslint-disable-next-line no-console
          console.warn('[P2P] engine load failed; continuing without peer sharing:', err);
        }).finally(() => {
          // Regardless of P2PML success/failure, we MUST call loadSource —
          // otherwise the player never starts. On failure we fall back to
          // plain hls.js with default loaders.
          if (!active) return;
          created.loadSource(playlistUrl);
        });
      } else {
        created.loadSource(playlistUrl);
      }
    };

    // Client-direct third-party HLS (Live TV #2): skip the mp4-envelope probe
    // entirely. The probe exists to detect OUR backend's `X-Stream-Container`
    // header and would otherwise send our auth headers to the external CDN. A
    // third-party HLS URL is always HLS — go straight to the hls.js path.
    if (crossOriginNoCreds) {
      startHlsPath();
      return () => {
        active = false;
        if (recoveryTimer) clearTimeout(recoveryTimer);
        if (instance) {
          instance.destroy();
          hlsRef.current = null;
          setHls(null);
        }
      };
    }

    // iOS Safari native-HLS path. iOS can't drive hls.js (see shouldUseNativeHls),
    // so mint a header-free signed playlist URL and let `video.src` play it
    // natively. VOD only — the /native.m3u8 mint exists for movie/TV/anime, not
    // live (sports/livetv stay on hls.js). We skip the mp4-envelope probe and go
    // straight to /native.m3u8 so the session resolves once, not twice.
    if (!live && shouldUseNativeHls(videoEl)) {
      void (async () => {
        const mintUrl = playlistUrl.replace('/playlist.m3u8', '/native.m3u8');
        let res: Response;
        try {
          res = await fetch(mintUrl, { headers: playbackHeaders(true) });
        } catch (err) {
          if (!active) return;
          console.error('[HLS] native mint failed', err);
          setFatal('networkError');
          return;
        }
        if (!active) return;
        if (!res.ok) {
          const bodyText = await res.text().catch(() => '');
          if (!active) return;
          if (isScreenLimitBody(res.status, bodyText)) {
            dispatch(screenLimitReached());
            setFatal('screenLimit');
            return;
          }
          setFatal(`playlist fetch failed (${res.status})`);
          return;
        }
        let parsed: { playlistUrl?: string };
        try {
          parsed = (await res.json()) as { playlistUrl?: string };
        } catch (err) {
          if (!active) return;
          console.error('[HLS] native mint parse failed', err);
          setFatal('native mint parse failed');
          return;
        }
        if (!active) return;
        if (!parsed.playlistUrl) { setFatal('native mint missing url'); return; }
        // Resolve the signed root-relative path against the API origin (the
        // absolute playlistUrl), same as the mp4 branch below.
        const absUrl = new URL(parsed.playlistUrl, playlistUrl).toString();
        const onError = (): void => { if (active) setFatal('mediaError'); };
        videoEl.crossOrigin = 'anonymous';
        videoEl.addEventListener('error', onError);
        videoEl.src = absUrl;
        nativeVideoEl = videoEl;
        nativeErrorHandler = onError;
        // AVPlayer is a black box to us — surface seeks that never settle.
        detachNativeDiag = attachNativeSeekDiag(videoEl);
        if (resumePosition > 0) {
          videoEl.addEventListener(
            'loadedmetadata',
            () => { try { videoEl.currentTime = resumePosition; } catch { /* ignore */ } },
            { once: true },
          );
        }
      })();
      return () => {
        active = false;
        if (recoveryTimer) clearTimeout(recoveryTimer);
        if (detachNativeDiag) detachNativeDiag();
        if (nativeVideoEl) {
          if (nativeErrorHandler) nativeVideoEl.removeEventListener('error', nativeErrorHandler);
          nativeVideoEl.removeAttribute('src');
          nativeVideoEl.load();
        }
      };
    }

    void (async () => {
      let res: Response;
      try {
        res = await fetch(playlistUrl, { headers: playbackHeaders(true) });
      } catch (err) {
        if (!active) return;
        console.error('[HLS] playlist probe failed', err);
        setFatal('networkError');
        return;
      }
      if (!active) return;
      if (!res.ok) {
        const bodyText = await res.text().catch(() => '');
        if (!active) return;
        if (isScreenLimitBody(res.status, bodyText)) {
          dispatch(screenLimitReached());
          setFatal('screenLimit');
          return;
        }
        setFatal(`playlist fetch failed (${res.status})`);
        return;
      }
      const container = res.headers.get('X-Stream-Container');
      if (container === 'mp4') {
        let parsed: { containerKind?: string; url?: string };
        try {
          parsed = (await res.json()) as { containerKind?: string; url?: string };
        } catch (err) {
          if (!active) return;
          console.error('[HLS] mp4 envelope parse failed', err);
          setFatal('mp4 envelope parse failed');
          return;
        }
        if (!active) return;
        if (!parsed.url) {
          setFatal('mp4 envelope missing url');
          return;
        }
        // Native byte-range progressive playback — no hls.js, no p2p. Setting
        // src on a video already wired to a previous instance is fine; the
        // browser tears down its internal media pipeline on src change.
        // Resolve the envelope URL against the playlist URL's origin so a
        // relative `/api/stream/proxy/...` path goes to the API host, not the
        // SPA host (where it would hit the Pages SPA fallback and the browser
        // would try to demux index.html as MP4).
        const absMp4Url = new URL(parsed.url, playlistUrl).toString();
        // crossOrigin: 'anonymous' makes the browser send a CORS request, so
        // the API's existing CORS allow-list governs the fetch. Without it,
        // a cross-origin <video src> hits the API's CORP same-origin header
        // and the load 0-byte-fails with DEMUXER_ERROR_COULD_NOT_OPEN.
        videoEl.crossOrigin = 'anonymous';
        videoEl.src = absMp4Url;
        mp4VideoEl = videoEl;
        return;
      }
      // Header absent OR explicitly 'hls' → existing HLS path.
      startHlsPath();
    })();

    return () => {
      active = false;
      if (recoveryTimer) clearTimeout(recoveryTimer);
      if (instance) {
        instance.destroy();
        hlsRef.current = null;
        setHls(null);
      }
      if (mp4VideoEl) {
        // Clear the src so the browser stops buffering after key/retry change.
        mp4VideoEl.removeAttribute('src');
        mp4VideoEl.load();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, retryToken, live, extraBuffer, crossOriginNoCreds, p2pMode?.kind,
    p2pMode?.kind === 'lobby' ? p2pMode.lobbyId : '',
    p2pMode?.kind === 'lobby' ? p2pMode.refKey : '',
    p2pMode?.kind === 'sports' ? p2pMode.eventId : '',
    p2pMode?.kind === 'sports' ? p2pMode.provider : '',
  ]);

  return { fatal, hls, p2pStats };
}
